/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B110030 担当者別取引日計表
 */
import {
  B000120EigyoNmFrom,
  B000120EigyoNmTo,
  B000130BumonNmFrom,
  B000130BumonNmTo,
  B000140ShainNmFrom,
  B000140ShainNmTo,
  catchFileError,
} from "clients/rest";
import { B110030GetExcel, B110030Request } from "clients/rest/b110/B110030";
import {
  msgState,
  SCodeSelect,
  SDateField,
  SFileButton,
  SFrame,
  SLabel,
  SNumberField,
  SText,
  STextField,
} from "components";
import { getClientError, getClientMessage } from "messages";
import {
  B110030OutputType,
  B110030ShokaKbn,
  getSCode,
  SCode,
} from "messages/codes";
import { labels } from "messages/labels";
import { B000130, B000140 } from "pages";
import { B000120 } from "pages/b000/B000120";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { date, download, yearMonthAt } from "services";
import { eigyoKengen } from "services/codes";
import { authState, focusState, menusState, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  denpyoYmdFrom: string;
  denpyoYmdTo: string;
  eigyoCdFrom: string;
  eigyoNmFrom: string;
  eigyoCdTo: string;
  eigyoNmTo: string;
  bumonCdFrom: string;
  bumonNmFrom: string;
  bumonCdTo: string;
  bumonNmTo: string;
  shainCdFrom: string;
  shainNmFrom: string;
  shainCdTo: string;
  shainNmTo: string;
  shokaKbn: SCode;
  outputType: SCode;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  denpyoYmdFrom: date(),
  denpyoYmdTo: date(),
  eigyoCdFrom: "",
  eigyoNmFrom: "",
  eigyoCdTo: "",
  eigyoNmTo: "",
  bumonCdFrom: "",
  bumonNmFrom: "",
  bumonCdTo: "",
  bumonNmTo: "",
  shainCdFrom: "",
  shainNmFrom: "",
  shainCdTo: "",
  shainNmTo: "",
  shokaKbn: getSCode(B110030ShokaKbn, "9"),
  outputType: getSCode(B110030OutputType, "0"),
};

const focusId = "B110030";

export const B110030 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });

  // フォーカス関数
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);

  // 画面遷移
  const navigate = useNavigate();

  // ログインユーザー情報
  const auth = useRecoilValue(authState);
  // 所属営業所コードでの営業所コード入力制御
  const [isEigyoCdOnly, setIsEigyoCdOnly] = useState(false);
  // menusステート取得
  const menus = useRecoilValue(menusState);
  const eigyosyoKengen = menus["110"].pages["110030"].eigyosyoKengen;

  // 初期表示イベント
  useEffect(() => {
    if (eigyosyoKengen === eigyoKengen.only) {
      // 自営業所コード(変更不可)の設定
      methods.setValue("eigyoCdFrom", auth?.eigyoCd);
      methods.setValue("eigyoCdTo", auth?.eigyoCd);
      // 入力部非活性化
      setIsEigyoCdOnly(true);
    }
    // 利用権限定義マスタ.営業所権限が"1"(全営業所指定可権限)の場合、
    else if (eigyosyoKengen === eigyoKengen.all) {
      // 空白(入力可能)
      setIsEigyoCdOnly(false);
    }
    focus(1);
  }, []);

  //Excel出力ボタンクリックイベント
  const handleClickExcelButton = () => {
    // Excel出力フォームのデータを取得する。
    const fields = methods.getValues();

    // 伝票日付が同月内でない場合。
    if (yearMonthAt(fields.denpyoYmdFrom) !== yearMonthAt(fields.denpyoYmdTo)) {
      setMsg(getClientError("B110030_001"));
      return;
    }

    const param: B110030Request = {
      denpyoYmdFrom: fields.denpyoYmdFrom,
      denpyoYmdTo: fields.denpyoYmdTo,
      eigyoCdFrom: fields.eigyoCdFrom,
      eigyoCdTo: fields.eigyoCdTo,
      bumonCdFrom: fields.bumonCdFrom,
      bumonCdTo: fields.bumonCdTo,
      shainCdFrom: fields.shainCdFrom,
      shainCdTo: fields.shainCdTo,
      shokaKbn: fields.shokaKbn?.value,
      outputType: fields.outputType?.value,
    };

    (async () => {
      await B110030GetExcel(param)
        .then((response) => {
          download(response);
          setMsg(getClientMessage("B110030_001"));
        })
        .catch((reason) => {
          catchFileError(reason, navigate, setMsg);
        });
      focus(1);
    })();
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="担当者別取引日計表"
        gridTemplateColumns="6rem 8rem 1rem 8rem 1rem 8rem 8rem"
      >
        <SLabel gridColumn="1" required>
          伝票日付
        </SLabel>
        <SDateField
          name="denpyoYmdFrom"
          label="伝票日付(開始)"
          {...register(1)}
          required
        />
        <SText>～</SText>
        <SDateField
          name="denpyoYmdTo"
          label="伝票日付(終了)"
          {...register(2)}
          required
        />

        <SLabel gridColumn="1" required>
          営業所
        </SLabel>
        <SNumberField
          name="eigyoCdFrom"
          label="営業所コード(開始)"
          code
          size={6}
          {...register(3)}
          onBlur={() => B000120EigyoNmFrom(methods, navigate, setMsg)}
          dialog={<B000120 />}
          handleSubmit={(data: any) => {
            methods.setValue("eigyoCdFrom", data.eigyoCd);
            methods.setValue("eigyoNmFrom", data.eigyoNmr);
          }}
          disabled={isEigyoCdOnly}
        />
        <STextField name="eigyoNmFrom" size={40} disabled gridColumn="span 2" />
        <SText>～</SText>
        <SNumberField
          name="eigyoCdTo"
          label="営業所コード(終了)"
          code
          size={6}
          {...register(4)}
          onBlur={() => B000120EigyoNmTo(methods, navigate, setMsg)}
          dialog={<B000120 />}
          handleSubmit={(data: any) => {
            methods.setValue("eigyoCdTo", data.eigyoCd);
            methods.setValue("eigyoNmTo", data.eigyoNmr);
          }}
          disabled={isEigyoCdOnly}
        />
        <STextField name="eigyoNmTo" size={40} disabled />

        <SLabel gridColumn="1">担当部門</SLabel>
        <SNumberField
          name="bumonCdFrom"
          code
          size={6}
          {...register(5)}
          onBlur={() => B000130BumonNmFrom(methods, navigate, setMsg)}
          dialog={<B000130 />}
          handleSubmit={(data: any) => {
            methods.setValue("bumonCdFrom", data.bumonCd);
            methods.setValue("bumonNmFrom", data.bumonNmr);
          }}
        />
        <STextField name="bumonNmFrom" size={40} disabled gridColumn="span 2" />
        <SText>～</SText>
        <SNumberField
          name="bumonCdTo"
          code
          size={6}
          {...register(6)}
          onBlur={() => B000130BumonNmTo(methods, navigate, setMsg)}
          dialog={<B000130 />}
          handleSubmit={(data: any) => {
            methods.setValue("bumonCdTo", data.bumonCd);
            methods.setValue("bumonNmTo", data.bumonNmr);
          }}
        />
        <STextField name="bumonNmTo" size={40} disabled />

        <SLabel gridColumn="1">担当者</SLabel>
        <SNumberField
          name="shainCdFrom"
          code
          size={7}
          {...register(7)}
          onBlur={() => B000140ShainNmFrom(methods, navigate, setMsg)}
          dialog={<B000140 />}
          handleSubmit={(data: any) => {
            methods.setValue("shainCdFrom", data.shainCd);
            methods.setValue("shainNmFrom", data.shainNm);
          }}
        />
        <STextField name="shainNmFrom" size={40} disabled gridColumn="span 2" />
        <SText>～</SText>
        <SNumberField
          name="shainCdTo"
          code
          size={7}
          {...register(8)}
          onBlur={() => B000140ShainNmTo(methods, navigate, setMsg)}
          dialog={<B000140 />}
          handleSubmit={(data: any) => {
            methods.setValue("shainCdTo", data.shainCd);
            methods.setValue("shainNmTo", data.shainNm);
          }}
        />
        <STextField name="shainNmTo" size={40} disabled />

        <SLabel gridColumn="1">消化区分</SLabel>
        <SCodeSelect
          name="shokaKbn"
          options={B110030ShokaKbn}
          {...register(9)}
        />

        <SLabel gridColumn="1">出力形式</SLabel>
        <SCodeSelect
          name="outputType"
          options={B110030OutputType}
          {...register(10)}
        />

        <SFileButton
          label={labels.buttons.excel}
          gridRow={8}
          gridColumn={7}
          align="right"
          {...register(11)}
          onClick={handleClickExcelButton}
        />
      </SFrame>
    </FormProvider>
  );
};
